import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, throwError } from 'rxjs';

import { AudioChannelCoreApiService } from '@shure/shared/angular/data-access/audio-channel-core';
import {
	ApolloQueryError,
	AudioChannelFeatureUpdateInput,
	AudioChannelUpdateInput,
	CombiningSubscriptionManager,
	DeviceResetScope,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { beamWidthToSysApiRecord, mapPolarPatternToSysApi } from '@shure/shared/angular/data-access/system-api/mappers';
import { BeamWidth, PolarPattern, Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { PositionPoint } from '@shure/shared/angular/utils/utils-position';

import {
	CoverageAudioChannelApiService,
	CoverageAudioChannelPostGateConfig
} from '../api/coverage-audio-channel-api.service';

import {
	CoverageAudioChannelSubscriptionGQL,
	UpdateCoverageAudioChannelMutationGQL
} from './graphql/generated/device-coverage-audio-channel.generated';
import { ResetDeviceMutationGQL } from './graphql/generated/device-coverage-device.generated';

@Injectable()
export class SysApiCoverageAudioChannelApiService implements CoverageAudioChannelApiService {
	public readonly audioChannelGqlSubscriptions: CombiningSubscriptionManager;

	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		subscriptionManagerService: SubscriptionManagerService,
		coverageAudioChannelSubscriptionGQL: CoverageAudioChannelSubscriptionGQL,
		private readonly updateCoverageAudioChannelMutationGQL: UpdateCoverageAudioChannelMutationGQL,
		private readonly resetDeviceMutationGQL: ResetDeviceMutationGQL,
		private readonly audioChannelApiService: AudioChannelCoreApiService
	) {
		this.logger = logger.createScopedLogger('SysApiCoverageAudioChannelApiService');

		this.audioChannelGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiCoverageApiService:AudioChannel',
				changeTypes: [
					NodeChangeType.AudioChannelLobeConfiguration,
					NodeChangeType.AudioChannelLobePosition,
					NodeChangeType.AudioChannelCoverageViewLock,
					NodeChangeType.AudioChannelName,
					NodeChangeType.AudioChannelLobeWidth,
					NodeChangeType.AudioChannelTalkerHeight,
					NodeChangeType.AudioChannelMute,
					NodeChangeType.AudioChannelPostGateMute,
					NodeChangeType.AudioChannelPreGateMute,
					NodeChangeType.AudioChannelGain,
					NodeChangeType.AudioChannelPostGateGain,
					NodeChangeType.AudioChannelMicPolarPattern,
					NodeChangeType.AudioChannelMicrophoneAngle,
					NodeChangeType.AudioChannelIdentify,
					NodeChangeType.AudioChannelDanteRouteTransmitter,
					NodeChangeType.AudioChannelLobeTalkerHeight,
					NodeChangeType.AudioChannelAutomixer
				],
				subscriptionGql: coverageAudioChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}
	public setAudioChannelsBeamWidth(channelIds: string[], width: BeamWidth): Observable<void> {
		return this.updateCoverageAudioChannel(
			channelIds.map((id) => ({ id, features: { lobeWidth: { width: beamWidthToSysApiRecord[width] } } }))
		);
	}

	public setAudioChannelPosition(channelId: string, position: PositionPoint): Observable<void> {
		this.logger.trace('setAudioChannelPosition()', 'Setting audio channel position', { channelId, position });

		return this.updateCoverageAudioChannel([
			{
				id: channelId,
				features: {
					lobePosition: {
						fixed: {
							xPosition: position.x.rawValue,
							yPosition: position.y.rawValue,
							zPosition: position.z?.rawValue
						}
					}
				}
			}
		]);
	}

	public setAudioChannelTalkerHeight(channelId: string, talkerHeight: number): Observable<void> {
		this.logger.trace('setAudioChannelTalkerHeight()', 'Setting audio channel talkerHeight', {
			channelId,
			talkerHeight
		});
		return this.updateCoverageAudioChannel([
			{
				id: channelId,
				features: {
					lobeTalkerHeight: { fixed: { height: talkerHeight } }
				}
			}
		]);
	}

	public setAudioChannelMicrophoneAngle(channelUpdates: { channelId: string; angle: number }[]): Observable<void> {
		this.logger.trace('setAudioChannelsMicrophoneAngle()', 'Setting audio channels microphone angle', {
			channelUpdates
		});

		const updates = channelUpdates.map((update) => ({
			id: update.channelId,
			features: { microphoneAngle: { fixed: { angle: update.angle } } }
		}));

		return this.updateCoverageAudioChannel(updates);
	}

	public setAudioChannelsMute(
		audioChannelMuteConfig: CoverageAudioChannelPostGateConfig[],
		muted: boolean
	): Observable<void> {
		this.logger.trace('setAudioChannelsMute()', 'Setting audio channels mute', { audioChannelMuteConfig, muted });

		return this.updateCoverageAudioChannel(
			audioChannelMuteConfig.map((config) => {
				const { channelId, isPostGate, isPreGate } = config;

				const features = isPreGate
					? { preGateMute: { muted } }
					: isPostGate
						? { postGateMute: { muted } }
						: { mute: { muted } };

				return {
					id: channelId,
					features
				};
			})
		);
	}

	public setAudioChannelGain(gainConfig: CoverageAudioChannelPostGateConfig[], gain: number): Observable<void> {
		this.logger.trace('setAudioChannelsGain()', 'Setting audio channels gain', { gainConfig, gain });

		return this.updateCoverageAudioChannel(
			gainConfig.map((config) => {
				const { channelId, isPostGate } = config;

				return { id: channelId, features: isPostGate ? { postGateGain: { gain } } : { gain: { gain } } };
			})
		);
	}

	public setAudioChannelName(channelId: string, name: string): Observable<Response<void, string>> {
		this.logger.trace('setAudioChannelName()', 'Setting audio channel name', { channelId, name });

		return this.updateCoverageAudioChannel([{ id: channelId, features: { name: { name: name } } }]).pipe(
			map(() => ({ state: ResponseState.Done })),
			catchError(() => {
				return of({ state: ResponseState.Error });
			})
		);
	}

	public setAudioChannelsIdentify(channelIds: string[], identifying: boolean): Observable<void[]> {
		return combineLatest(
			channelIds.map((id) => this.audioChannelApiService.setAudioChannelIdentify(id, identifying))
		);
	}

	public setAudioChannelsCoverageViewLock(channelIds: string[], enabled: boolean): Observable<void> {
		return this.updateCoverageAudioChannel(
			channelIds.map((id) => ({ id, features: { coverageViewLock: { enabled } } }))
		);
	}

	public setAudioChannelsPolarPattern(channelIds: string[], polarPattern: PolarPattern): Observable<void> {
		this.logger.trace('setAudioChannelsPolarPattern()', 'Setting audio channels polar pattern', {
			channelIds,
			polarPattern
		});

		return this.updateCoverageAudioChannel(
			channelIds.map((id) => ({
				id,
				features: { micPolarPattern: { pattern: mapPolarPatternToSysApi(polarPattern) } }
			}))
		);
	}

	public addAudioChannels(channelIds: string[]): Observable<void> {
		this.logger.trace('addAudioChannels()', 'Adding audio channels', { channelIds });
		return this.setAudioChannelsEnabled(channelIds, true);
	}

	public removeAudioChannels(channelIds: string[]): Observable<void> {
		this.logger.trace('removeAudioChannels()', 'Removing audio channels', { channelIds });
		return this.setAudioChannelsEnabled(channelIds, false);
	}

	public resetLobePosition(channelId: string): Observable<void> {
		this.logger.trace('resetLobePosition()', 'Resetting lobe position', { channelId });
		return this.resetDeviceMutationGQL
			.mutate({
				variables: {
					id: channelId,
					scope: DeviceResetScope.Lobe,
					injectKeyring: '//supplied-by-keyring-injector'
				}
			})
			.pipe(
				map((result) => {
					if (
						result.data?.resetDevice &&
						'error' in result.data.resetDevice &&
						result.data.resetDevice.error
					) {
						throw new Error(result.data.resetDevice.error.message ?? 'Reset failed');
					}
					return;
				}),
				catchError((error: Error) => {
					this.logger.error('resetLobePosition()', 'Failed to reset lobe position', { channelId, error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public updateChannelProperty(channelId: string, update: AudioChannelFeatureUpdateInput): Observable<void> {
		return this.updateCoverageAudioChannel([{ id: channelId, features: update }]);
	}

	private setAudioChannelsEnabled(channelIds: string[], enabled: boolean): Observable<void> {
		this.logger.trace('setAudioChannelsEnabled()', 'Setting audio channels enabled state', { channelIds, enabled });
		return this.updateCoverageAudioChannel(
			channelIds.map((id) => ({ id, features: { lobeConfiguration: { enabled } } }))
		);
	}

	private updateCoverageAudioChannel(updates: AudioChannelUpdateInput[]): Observable<void> {
		this.logger.trace('updateCoverageAudioChannel()', 'Updating audio channel', { updates });
		return this.updateCoverageAudioChannelMutationGQL
			.mutate({
				variables: {
					updates: updates.map((update) => ({ audioChannel: update })),
					injectKeyring: '//supplied-by-keyring-injector'
				}
			})
			.pipe(
				map((result) => {
					const errors = result.data?.updateNodes.filter((e) => 'error' in e && e.error !== null) ?? [];
					if (errors?.length > 0) {
						const message = errors
							.map((fail) => ('error' in fail ? (fail.error?.message ?? '') : ''))
							.join('\n');

						throw new Error(message);
					}

					this.logger.trace('updateCoverageAudioChannel()', 'Done', { updates });
					return;
				}),
				catchError((error: Error) => {
					this.logger.error('updateCoverageAudioChannel()', 'Failed to update audio channel', {
						updates,
						error
					});
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}
}
